import { Popover, Select, Slider, Stack, TextField, Typography } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import { IconButton } from '@mui/material';
import { t } from 'i18next';
import PendingIcon from '@mui/icons-material/PendingOutlined';

import { IColorbar } from 'features/seismic/models/interfaces/IColorbar';
import { IAbsoluteGain, useSeismicStore } from 'features/seismic/stores/useSeismicStore';
import { SeismicColorbarSelect } from '../SeismicColorbarSelect';
import { AbsoluteGainMenu } from '../AbsoluteGainMenu';
import { useLine3DNavigationStore } from 'features/seismic/stores/useLine3DNavigationStore';
import { useLoadingTileStore } from 'features/seismic/stores/useLoadingTilesStore';

export type AttributeSettingsMenuProps = {
    colorbar: IColorbar,
    onChangeColorbar: (value: IColorbar) => void,
    opacity: number,
    onChangeOpacity: (value: number) => void,
    gain: IAbsoluteGain,
    onChangeGain: (value: IAbsoluteGain) => void,
    defaultGain: IAbsoluteGain | null | undefined,
    disabled?: boolean,
    additionalColorbars?: IColorbar[],
    enableColorbar?: boolean,
    enableGain?: boolean,
};

export const AttributeSettingsMenu = ({ colorbar, onChangeColorbar, gain, onChangeGain, opacity, onChangeOpacity, defaultGain, disabled = false,
    additionalColorbars = [], enableColorbar = true, enableGain = true} : AttributeSettingsMenuProps) => {
    const iconButtonRef = useRef<HTMLButtonElement>(null);
    const [opened, setOpened] = useState(false);

    const isLoading = useLoadingTileStore(state => !!state.loading);

    const scaleOpacity = useCallback((value: number) => Math.round(value * 100) , []);
    const formatOpacity = useCallback((value: number) => value.toString() + '%', []);

    return (
        <React.Fragment>
            <IconButton
                ref={iconButtonRef}
                title={'Product settings'}
                color='primary'
                onClick={() => setOpened(!opened)}
                disabled={disabled}
            >
                <SettingsIcon/>
            </IconButton>
            <Popover
                anchorEl={iconButtonRef.current}
                open={opened}
                onClose={() => setOpened(false)}
                keepMounted
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
            >
                <Stack gap={4} style={{ backgroundColor: '#041f3b'}} padding={3} direction='column' justifyContent='flex-start' visibility={opened ? 'visible': 'hidden'}>
                    {
                        enableColorbar
                        &&
                        <Stack>
                            <Typography color='white'> {t('colorbarTitle').toString()} </Typography>
                            <SeismicColorbarSelect disabled={isLoading} colorbar={colorbar} onChange={onChangeColorbar} additionalColorbars={additionalColorbars}/>
                        </Stack>
                    }
                    <Stack>
                        <Typography color='white'>
                            Opacity: {formatOpacity(scaleOpacity(opacity))}
                        </Typography>
                        <Slider
                            title={t('opacity')}
                            size='small'
                            min={0}
                            max={1}
                            step={0.05}
                            value={opacity}
                            onChange={(event, value) => setTimeout(() => onChangeOpacity(value as number), 0)}
                            scale={(value: number) => value * 100}
                            valueLabelFormat={(value: number) => value.toString() + '%'}
                        />
                    </Stack>
                    {
                        !!defaultGain
                        &&
                        enableGain
                        &&
                        <Stack gap={2}>
                            <Typography color='white'>{t('gainTitle').toString()}</Typography>
                            <AbsoluteGainMenu disabled={isLoading} absoluteGain={gain} defaultAbsoluteGain={defaultGain} onChange={onChangeGain}/>
                        </Stack>
                    }
                </Stack>
            </Popover>
        </React.Fragment>
    );
};